import { Button, DropdownSeparator, RadioGroup, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkType } from '@/api/rest/resources/types/benchmark';
import { RadioItem } from '@/components/widgets/a/RadioItem/RadioItem';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

type ChartBenchmarkProps = {
  selectedBenchmark?: string | null;
  setSelectedBenchmark?: (id: string | null) => void;
  onOpenDialog?: () => void;
  indicatorType?: string;
};

export const ChartBenchmark = ({
  selectedBenchmark,
  setSelectedBenchmark,
  onOpenDialog,
  indicatorType,
}: ChartBenchmarkProps) => {
  const { t } = useTranslation();

  const organizationId = useOrganizationId();
  const { data: response } = useGetCustomBenchmarks(organizationId, indicatorType);
  const customBenchmarks = response?.results || [];

  const handleValueChange = (newValue: string | boolean | null | undefined) => {
    if (typeof newValue !== 'string') return;

    if (newValue === CustomBenchmarkType.SCIENTIFIC) {
      setSelectedBenchmark?.(null);
      return;
    }

    if (selectedBenchmark === newValue) {
      setSelectedBenchmark?.(null);
    } else {
      setSelectedBenchmark?.(newValue);
    }
  };

  const isScientificActive = !selectedBenchmark || selectedBenchmark === CustomBenchmarkType.SCIENTIFIC;

  return (
    <Stack direction='col'>
      <RadioGroup
        value={selectedBenchmark || CustomBenchmarkType.SCIENTIFIC}
        onValueChange={handleValueChange}
        data-testid='benchmark-radio-buttons'
        onClick={(e) => e.stopPropagation()}
        className='gap-0'
      >
        <RadioItem
          label={t('graph.benchmark.radioLabel.scientific', 'Scientific')}
          value={CustomBenchmarkType.SCIENTIFIC}
          isChecked={isScientificActive}
        />

        {customBenchmarks.map((benchmark) => (
          <RadioItem
            key={benchmark.id}
            label={benchmark.name}
            value={benchmark.id}
            isChecked={selectedBenchmark === benchmark.id}
          />
        ))}
      </RadioGroup>

      <DropdownSeparator className='mx-2' />
      <Stack className='px-3 pb-2 pt-1 items-start'>
        <Button
          variant='outline'
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            onOpenDialog?.();
          }}
          data-testid='set-custom-benchmark-btn'
        >
          {t('graph.benchmark.buttonLabel')}
        </Button>
      </Stack>
    </Stack>
  );
};
